import { ParticipantTypeEnum } from "@/enums/RecapEnums";
import i18n from "@/shared/i18n";

export interface IRecapState {
  selectedSession: string | null;
  error: null | string;
  token: null | string;
  loading: boolean;
  userInfo: null | any;
  selectedRecording: IRecording;
  recordings: IRecording[];
  sessions: Session[];
  participants: {
    totalCount: number;
    data: IParticipant[];
  };
  absence: {
    totalCount: number;
    data: IParticipant[] | IAbsence[];
  };
  summary: ISummary[];
  actionItems: IActionItems[];
  isShowVideoDrawer: boolean;
}

export interface IAbsence {
  id: string;
  fullName: string;
}
export interface ISummary {
  id: string;
  title: string;
  summary: string;
}
export interface IParticipant {
  nickname: string;
  role: string;
  userId: string;
  user: IUser;
  presences: IPresence[];
  statistics: [];
}

export interface IPresence {
  start: string;
  end: string;
}
export interface IUser {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  pictureUrlVm: {
    url: string;
    thumbnailUrl: string;
  };
  username: string;
  fullName: string;
  jobTitle: string;
  role: string;
  type: string;
  isActive: boolean;
}
export interface IRecording {
  id: string;
  file: string;
  thumbnail: string;
  expiresAt: string;
  start: string;
  end: string;
  transcription: RecapTranscriptItem;
}

export type Session = {
  id: string;
  start: string;
  end: string;
  meetId: string;
  title: string;
  summary: string;
};

export type RecapTranscriptItem = {
  start: string;
  end: string;
  text: string;
  isGuest: boolean;
  speakerJId: string;
  fullName: string;
  speaker: string;
};

export interface RecapTranscriptsProps {
  transcripts?: {
    totalCount: number;
    data: RecapTranscriptItem[];
  };
}

export interface IActionItems {
  id: string;
  title: string;
  description: string;
  assigneeName: string;
  user: IUser;
  userId: string;
  priority?: number | null | string;
  dueDate?: string | null;
  taskCode?: string | number | null;
  taskId?: string | null;
  taskUrl?: string | null;
  createdFromRosa?: boolean;
  isDeleted?: boolean;
  updateDate?: string | null;
}

export type RecapActionItemsProps = {
  actionItems?: IActionItems[];
  isLoading?: boolean;
  onEdit: (item: IActionItems) => void;
  sessionId: string;
  showDeleted?: boolean;
};

export type EngagementSegment = {
  id: number;
  position: {
    insetInlineStart: number;
    width: number;
  };
  tooltip: string;
};

export type RecapOverview = {
  duration?: {
    duration?: number;
    startAt?: string | null;
    endAt?: string | null;
    durationText?: string;
    description?: string;
  };
  firstJoin?: {
    date?: string | null;
    delay?: number;
    time?: string;
    description?: string;
  };
  lastLeave?: {
    date?: string | null;
    overrun?: number;
    time?: string;
    description?: string;
  };
  participants?: {
    participantCount?: number;
    avgSpokeTime?: number;
    avgSpokeTimeText?: string;
    description?: string;
  };
  engagement?: {
    engagementPercents?: number;
    spokenUserCount?: number;
    percentText?: string;
    speakingParticipants: number;
  };
  conductingStatusValues?: {
    delayTime: string;
    durationTime: string;
    engagementPercent: number;
    speakingParticipants: number;
    totalParticipants: number;
  };
};

export type RecapKPICardsProps = {
  overview?: RecapOverview | null;
  isLoading?: boolean;
};

export type RecapPrintData = {
  meetTitle?: string;
  selectedSessionLabel?: React.ReactNode;
  participants?: any;
  recapOverview?: any;
  summary?: string;
};

export type RecapHeaderProps = {
  meetTitle: string;
  selectedSession: string;
  onSessionChange: (value: string) => void;
  placeholder: string;
  options: {
    value: string;
    label: React.ReactNode;
    selectedLabel?: React.ReactNode;
    plainLabel?: string;
  }[];
  eventType?: number;
  overview?: string;
  participants?: any;
  isLoadingParticipants?: boolean;
  printData?: RecapPrintData;
};

export type RecapParticipantsProps = {
  organizerName?: string;
  attendedLabel?: string;
  guestsLabel?: string;
  joinedViaCallLabel?: string;
};

export type DecisionItem = {
  id: string;
  sessionId: string;
  description: string;
  title: string;
  createdByRosa: boolean;
  isDeleted: boolean;
  updateDate?: string | null;
};

export const RecapAttendanceTags = () => [
  {
    label: i18n.t("recap.attendee"),
    value: ParticipantTypeEnum.Attendee,
    color: "#52C41A",
    backgroundColor: "#F6FFED",
  },
  {
    label: i18n.t("recap.joinedViaCall"),
    value: ParticipantTypeEnum.JoinViaCall,
    color: "#FA8C16",
    backgroundColor: "#FFF7E6",
  },
  {
    label: i18n.t("recap.guest"),
    value: ParticipantTypeEnum.Guest,
    color: "var(--color-primary)",
    backgroundColor: "var(--color-primary-light)",
  },
  {
    label: i18n.t("recap.absent"),
    value: ParticipantTypeEnum.Absent,
    color: "#FA541C",
    backgroundColor: "#FFF2E8",
  },
];

export type RecapAttendanceTagProps = {
  value: ParticipantTypeEnum;
};

export type RecapActionItemModalProps = {
  open: boolean;
  mode: "add" | "edit";
  actionItem?: IActionItems | null;
  assigneeOptions: {
    label: string;
    value: string;
  }[];
  onClose: () => void;
  onSave: (values: { description: string; assignee?: string }) => void;
};

export interface RecapContentProps {
  recapSessions?: any[];
  isSessionsLoading?: boolean;
  fetchRecapSessionsByMeetKey?: () => void;
}

export type AttendanceRow = {
  key: string;
  id: string;
  type: "present" | "absent";
  fullName: string;
  nickname?: string;
  userId?: string;
  jobTitle?: string;
  firstJoin?: string;
  lastLeave?: string;
  inMeetingDuration?: string;
  inMeetingSpeechTime?: string;
  segments: EngagementSegment[];
  participantType?: ParticipantTypeEnum;
  avatar?: string;
};

export type AttendeeItem = {
  id: string;
  fullName: string;
  description?: string;
  avatar?: string;
  status: ParticipantTypeEnum;
};

export interface ModifyActionItems {
  id?: string;
  participants?: any[];
  data: {
    sessionId: string;
    title: string;
    description?: string;
    assigneeName: string;
    dueDate?: string;
    priority?: string;
    sourceTime?: string;
  };
}

export interface ModifyDecisions {
  sessionId?: string;
  id?: string | number;
  title: string;
}

export interface SessionInformationProps {
  selectedSessionId?: string;
  currentSession?: any;
  participants?: any;
  isLoadingParticipants?: boolean;
  minutes?: string;
}

export interface ActionItemProps {
  open: boolean;
  data?: IActionItems | null;
  sessionId: string;
  onClose: () => void;
  participants?: any;
}

export enum TaskItemPriorityEnum {
  Select = 0,
  High = 1,
  Medium = 2,
  Low = 3,
}

export interface IConvertToTaskProps {
  open?: boolean;
  actionItem: IActionItems;
  sessionId?: string;
  onCloseModal?: () => void;
  onSaveStatus?: (type: string, text: string) => void;
}

export type SectionHeaderProps = {
  title: React.ReactNode;
  icon?: string;
  iconColor?: string;
};
